import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardBody, Col, Row, Spinner, Button } from "reactstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { components } from "react-select";
import Select from "react-select";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* ---------------- SHIFT MAP ---------------- */

const SHIFT_MAP = {
  "SHIFT A": 1,
  "SHIFT B": 2,
  "SHIFT C": 3,
};

const SHIFT_OPTIONS = Object.keys(SHIFT_MAP);

/* ---------------- UTILS ---------------- */

const getTodayDate = () => new Date().toISOString().split("T")[0];

const getShiftByTime = () => {
  const h = new Date().getHours();
  if (h >= 6 && h < 14) return "SHIFT A";
  if (h >= 14 && h < 22) return "SHIFT B";
  return "SHIFT C";
};

/* ---------------- COMPONENT ---------------- */

const StationCycleTime = () => {
  const [chartData, setChartData] = useState([]);
  const [stations, setStations] = useState([]);
  const [stationKeys, setStationKeys] = useState([]);
  const [selectedStations, setSelectedStations] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedShift, setSelectedShift] = useState(getShiftByTime());


const CustomValueContainer = ({ children, ...props }) => {
  const selected = props.getValue();
  const totalOptions = props.selectProps.options.length;

  let displayText = "Select stations...";

  if (selected.length === totalOptions && totalOptions > 0) {
    displayText = `All stations selected (${totalOptions})`;
  } else if (selected.length > 3) {
    displayText = `${selected.length} stations selected`;
  } else if (selected.length > 0) {
    displayText = selected.map(s => s.label).join(", ");
  }

  return (
    <components.ValueContainer {...props}>
      <span style={{ color: "#fff", fontSize: "13px" }}>
        {displayText}
      </span>
      {children[1] /* keeps dropdown arrow */}
    </components.ValueContainer>
  );
};

  const stationOptions = stationKeys.map((k) => ({
    value: k,
    label: k,
  }));

  /* ---------------- FETCH DATA ---------------- */

  const fetchStationCycleTime = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `https://api.hanoch.in/api/mcc-st-data/?machine_id=74&date=${selectedDate}&shift=${SHIFT_MAP[selectedShift]}`
      );

      const apiData = res.data || [];

      if (!apiData.length) {
        setChartData([]);
        return;
      }

      /* -------- COLLECT STATION KEYS (ONCE) -------- */

      const keys = Array.from(
        new Set(
          apiData.flatMap((row) =>
            row.st_values.map((s) => s.key)
          )
        )
      );

      setStationKeys(keys);
      setSelectedStations((prev) => (prev.length ? prev : keys));

      /* -------- BUILD CHART DATA -------- */

      const formattedData = apiData.map((row) => {
        const obj = {
          time: row.time.slice(0, 5), // HH:mm
        };

        row.st_values.forEach((st) => {
          obj[st.key] = Number(st.value);
        });

        return obj;
      });

      setStations(keys.map((k) => ({ key: k })));
      setChartData(formattedData);
    } catch (err) {
      console.error("Station cycle time error:", err);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStationCycleTime();
    const interval = setInterval(fetchStationCycleTime, 60000);
    return () => clearInterval(interval);
  }, [selectedDate, selectedShift]);

  /* ---------------- EXPORT EXCEL ---------------- */

  const exportToExcel = () => {
    if (!chartData.length) return;

    const rows = chartData.map((row) => ({
      Date: selectedDate,
      Shift: selectedShift,
      ...row,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Station Cycle Time");

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `Station_Cycle_Time_${selectedDate}_${selectedShift}.xlsx`
    );
  };

  /* ---------------- RENDER ---------------- */

  return (
    <Card className="">
      <CardBody>
        <h5 className="text-center fw-bold mb-3">
          STATION CYCLE TIME GRAPH
        </h5>

        <Row className="mb-3 align-items-end">
          <Col md={2}>
             <label className="mb-1">Date</label>
            <input
              type="date"
              className="form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </Col>

          <Col md={2}>
           <label className="mb-1">Shift</label>
            <select
              className="form-control"
              value={selectedShift}
              onChange={(e) => setSelectedShift(e.target.value)}
            >
              {SHIFT_OPTIONS.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Col>

          <Col md={6}>
             <label className="mb-1">Select Stations</label>
           <Select
  isMulti
  closeMenuOnSelect={false}
  hideSelectedOptions={false}
  controlShouldRenderValue={false}   // 🔑 hides chips
  options={stationOptions}
  value={stationOptions.filter(o =>
    selectedStations.includes(o.value)
  )}
  onChange={(sel) =>
    setSelectedStations(sel.map(s => s.value))
  }
  components={{
    ValueContainer: CustomValueContainer
  }}
  // placeholder="Select stations..."
  classNamePrefix="react-select"
   styles={{
        control: (base) => ({
          ...base,
          // backgroundColor: "#252b3b",
          // borderColor: "#334155",
          minHeight: 40,
          height: 40,
        }),
        valueContainer: (base) => ({
          ...base,
          height: 40,
          display: "flex",
          alignItems: "center",
          paddingLeft: 10,
        }),
        indicatorsContainer: (base) => ({
          ...base,
          height: 40,
        }),
      }}
/>

          </Col>
 
          
          
          <Col md={2}>
            <Button color="success" onClick={exportToExcel}>
              Export Excel
            </Button>
          </Col>
        </Row>

       
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip
  itemSorter={(item) => -item.value}
/>

              <Legend />
              {stationKeys
                .filter((k) => selectedStations.includes(k))
                .map((key, i) => (
                  <Line
                    key={key}
                    dataKey={key}
                    dot={false}
                    connectNulls
                    type="monotone"
                    stroke={`hsl(${i * 35},70%,55%)`}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        
      </CardBody>
    </Card>
  );
};

export default StationCycleTime;
